import React, {
  useState,
  useRef,
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import {
  Button,
  Flex,
  Heading,
  HStack,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useCampaignCreate } from "views/admin/campaign_create/roas/components/CampaignCreateContext";
import GamelightButton from "components/button";
import { FaPencilAlt, FaPlay } from "react-icons/fa";
import { useStep } from "views/admin/campaign_create/roas/components/StepContext";
import { Delete, Post, url } from "api";
import { useQuery } from "hooks/useQuery";
import Lottie from "react-lottie";

import * as animationData from "../../../../../assets/animations/coin-animated.json";
import { useConfirmDialog } from "components/confirmModal";
import {
  hideGlobalLoading,
  showGlobalLoading,
} from "helper/GlobalLoadingDispatches";

export const GRADIENT_BUTTON_1 = "#fc0fa5";
export const GRADIENT_BUTTON_2 = "#ff4f4f";
export const GRADIENT_BUTTON_3 = "#ff7d44";
export const PURPLE = "#7B00FF";

export function hexToRGB(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const Creatives: React.FC = () => {
  const searchParams = useQuery();
  const campaignId = searchParams.get("id");

  const toast = useToast();
  const { nextStep } = useStep();
  const {
    campaign,
    creatives,
    handleUpdateCreativeFiles,
    handleUpdateCreatives,
  } = useCampaignCreate();

  const [defaultCreatives, setDefaultCreatives] = useState<{
    bannerImageUrl: string;
    bannerVideoUrl: string;
  }>({
    bannerImageUrl: "",
    bannerVideoUrl: "",
  });
  const [removingBannerImage, setRemovingBannerImage] = useState(false);
  const [removingBannerVideo, setRemovingBannerVideo] = useState(false);
  const [bannerImage, setBannerImage] = useState<string | null>(null);
  const [bannerImageFile, setBannerImageFile] = useState<File | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);

  const [uploadingCreatives, setUploadingCreatives] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    ConfirmDialog: RemoveBannerImageConfirmDialog,
    openDialog: openRemoveBannerImageDialog,
  } = useConfirmDialog({
    title: "Banner is being removed",
    description:
      "You are about to remove the banner image. This action can not be undone.",
  });

  const {
    ConfirmDialog: RemoveBannerVideoConfirmDialog,
    openDialog: openRemoveBannerVideoDialog,
  } = useConfirmDialog({
    title: "Video is being removed",
    description:
      "You are about to remove the banner video. This action can not be undone.",
  });

  const bannerInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const isDefaultCreatives = useMemo(() => {
    return {
      bannerImage:
        creatives?.bannerImageUrl === defaultCreatives.bannerImageUrl,
      bannerVideo:
        creatives?.bannerVideoUrl === defaultCreatives.bannerVideoUrl,
    };
  }, [
    creatives?.bannerImageUrl,
    creatives?.bannerVideoUrl,
    defaultCreatives.bannerImageUrl,
    defaultCreatives.bannerVideoUrl,
  ]);

  const handlePlayVideo = () => {
    onOpen();
    if (videoRef.current) {
      videoRef.current.play();
    }
  };

  const handleCloseModal = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    onClose();
  };

  const handleBannerUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type.includes("image/")) {
      setBannerImageFile(file);
      setBannerImage(URL.createObjectURL(file));
    } else {
      toast({
        title: "Invalid file type",
        description: "Please upload an image file.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleVideoUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith("video/")) {
      setVideoFile(file);
      setVideoPreview(URL.createObjectURL(file));
    } else {
      toast({
        title: "Invalid file type",
        description: "Please upload a video file.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const triggerBannerUpload = () => {
    bannerInputRef.current?.click();
  };

  const triggerVideoUpload = () => {
    if (!removingBannerVideo && (bannerImage || creatives.bannerImageUrl)) {
      videoInputRef.current?.click();
    }
  };

  const handleValidateStoreURL = useCallback(
    async (url: string) => {
      if (!url) return;

      try {
        const response = await Post("/dashboard/validate/app", {
          playStoreUrl: url,
        });

        const { bannerImageUrl, bannerVideoUrl } = response;

        setDefaultCreatives({
          bannerImageUrl,
          bannerVideoUrl,
        });
      } catch (error) {
        console.error(error);
        toast({
          title: "Error",
          description: "An error occurred while validating the store URL.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    },
    [toast]
  );

  const handleRemoveBannerImage = async () => {
    try {
      if (campaignId) {
        const result = await openRemoveBannerImageDialog();
        if (!result) return;

        setRemovingBannerImage(true);
        const response = await Delete(
          `/dashboard/campaigns/${campaignId}/creatives/banner`
        );

        if (response.err) {
          toast({
            title: "Error",
            description:
              response.err ?? "An error occurred while removing banner image.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });

          return;
        }

        handleUpdateCreatives({
          bannerImageUrl: defaultCreatives?.bannerImageUrl ?? "",
        });
        setBannerImage(defaultCreatives?.bannerImageUrl);

        toast({
          title: "Banner image removed successfully!",
          status: "success",
        });
      } else {
        setBannerImage(null);
        setBannerImageFile(null);
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "An error occurred while removing the banner image.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setRemovingBannerImage(false);
    }
  };

  const handleRemoveBannerVideo = async () => {
    try {
      if (campaignId) {
        const result = await openRemoveBannerVideoDialog();
        if (!result) return;

        setRemovingBannerVideo(true);
        const response = await Delete(
          `/dashboard/campaigns/${campaignId}/creatives/video`
        );

        if (response.err) {
          toast({
            title: "Error",
            description:
              response.err ?? "An error occurred while removing banner video.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });

          return;
        }

        handleUpdateCreatives({
          bannerVideoUrl: defaultCreatives?.bannerVideoUrl ?? "",
        });
        setVideoPreview(defaultCreatives?.bannerVideoUrl);

        toast({
          title: "Banner video removed successfully!",
          status: "success",
        });
      } else {
        setVideoFile(null);
        setVideoPreview(null);
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "An error occurred while removing the banner video.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setRemovingBannerVideo(false);
    }
  };

  const handleSaveCreatives = async () => {
    try {
      if (campaignId) {
        if (!bannerImageFile && !videoFile) {
          /* toast({
            title: "Creatives saved successfully!",
            status: "success",
          }); */
          nextStep(true);
          return;
        }
        showGlobalLoading("Uploading creatives");

        setUploadingCreatives(true);
        if (bannerImageFile) {
          const arrayBuffer = await bannerImageFile.arrayBuffer();
          const myHeaders = new Headers();
          myHeaders.append(
            "Content-Disposition",
            `attachment;filename=banner.png`
          );
          myHeaders.append("Content-Type", "image/png");
          myHeaders.append(
            "Authorization",
            "Bearer " + localStorage.getItem("jwtToken")
          );

          const response = await fetch(
            `${url}/dashboard/campaigns/${campaignId}/creatives/banner`,
            {
              method: "PUT",
              body: arrayBuffer,
              headers: myHeaders,
            }
          );

          const json = await response
            .clone()
            .json()
            .catch((e) => response.text());

          if (json.err) {
            toast({
              title: "Error",
              description: json.err,
              status: "error",
              duration: 5000,
              isClosable: true,
            });

            return;
          }
          handleUpdateCreatives({
            bannerImageUrl: URL.createObjectURL(bannerImageFile),
          });
          setBannerImage(
            bannerImageFile ? URL.createObjectURL(bannerImageFile) : null
          );
          setBannerImageFile(null);
        }

        if (videoFile) {
          const arrayBuffer = await videoFile.arrayBuffer();
          const myHeaders = new Headers();
          myHeaders.append(
            "Content-Disposition",
            `attachment;filename=video.mp4`
          );
          myHeaders.append("Content-Type", "video/mp4");
          myHeaders.append(
            "Authorization",
            "Bearer " + localStorage.getItem("jwtToken")
          );
          const response = await fetch(
            `${url}/dashboard/campaigns/${campaignId}/creatives/video`,
            {
              method: "PUT",
              body: arrayBuffer,
              headers: myHeaders,
            }
          );

          const json = await response
            .clone()
            .json()
            .catch((e) => response.text());

          if (json.err) {
            toast({
              title: "Error",
              description: json.err,
              status: "error",
              duration: 5000,
              isClosable: true,
            });

            return;
          }
          handleUpdateCreatives({
            bannerVideoUrl: URL.createObjectURL(videoFile),
          });
          setVideoPreview(videoFile ? URL.createObjectURL(videoFile) : null);
          setVideoFile(null);
        }
        nextStep(true);

        toast({
          title: "Creatives saved successfully!",
          status: "success",
        });
      } else {
        nextStep(true);
        handleUpdateCreativeFiles({
          bannerImage: bannerImageFile,
          bannerVideo: videoFile,
        });
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "An error occurred while saving the creatives.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUploadingCreatives(false);
      hideGlobalLoading();
    }
  };

  useEffect(() => {
    handleValidateStoreURL(campaign?.playstoreUrl);
  }, [
    campaign?.playstoreUrl,
    handleValidateStoreURL,
    creatives.bannerImageUrl,
    creatives.bannerVideoUrl,
  ]);

  return (
    <Flex
      flexDir="column"
      flex={1}
      alignItems="center"
      justify="center"
      h={`calc(88vh - 200px)`}
    >
      <Flex
        flex={1}
        flexDir="column"
        alignItems="center"
        justify="center"
        w="full"
        gap={8}
      >
        <Flex flex={1} w="full" gap={8}>
          <Flex
            flex={1}
            flexDir="column"
            justifyContent="center"
            alignItems="flex-start"
            gap={4}
          >
            <input
              type="file"
              disabled={uploadingCreatives}
              ref={bannerInputRef}
              onChange={handleBannerUpload}
              accept="image/*"
              style={{ display: "none" }}
            />
            <input
              type="file"
              disabled={uploadingCreatives}
              ref={videoInputRef}
              onChange={handleVideoUpload}
              accept="video/*"
              style={{ display: "none" }}
            />
            <Flex
              gap={4}
              justifyContent="flex-start"
              alignItems="center"
              w="full"
              px={4}
              py={8}
              borderRadius="lg"
              transition="all 0.5s ease"
              _hover={{
                backgroundColor: "gray.100",
                cursor: "pointer",
              }}
              onClick={triggerBannerUpload}
            >
              <Flex
                boxSize={24}
                bgColor={
                  creatives.bannerImageUrl || bannerImage
                    ? "whatsapp.500"
                    : "gray.300"
                }
                borderRadius="full"
                alignItems="center"
                justifyContent="center"
              >
                <Heading
                  color={
                    creatives.bannerImageUrl || bannerImage
                      ? "white"
                      : "gray.700"
                  }
                >
                  1
                </Heading>
              </Flex>
              <Flex
                flexDir="column"
                justifyContent="center"
                px={4}
                borderLeftWidth={1}
                borderLeftColor="gray.200"
                w="70%"
              >
                <HStack spacing={4}>
                  <Heading color="gray.700">Banner</Heading>
                  <IconButton
                    isRound={true}
                    variant="solid"
                    colorScheme={"brand"}
                    aria-label="upload"
                    fontSize="16px"
                    size="sm"
                    icon={<FaPencilAlt style={{ marginBottom: 1 }} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      triggerBannerUpload();
                    }}
                  />
                  {bannerImageFile && (
                    <Tooltip label="This button will allow you to clear the banner image you've selected just before.">
                      <Button
                        variant={"link"}
                        colorScheme="red"
                        fontWeight="semibold"
                        textAlign={"left"}
                        zIndex={10}
                        onClick={(e) => {
                          e.stopPropagation();
                          setBannerImage(null);
                          setBannerImageFile(null);
                          bannerInputRef.current!.value = "";
                        }}
                      >
                        Clear Selection
                      </Button>
                    </Tooltip>
                  )}
                </HStack>
                <Text color="gray.500" fontWeight="medium">
                  {bannerImage || creatives.bannerImageUrl
                    ? "Banner image set successfully. Click to select a different image (16:9 Aspect Ratio)"
                    : "Select a banner image for your campaign (16:9 Aspect Ratio)"}
                </Text>
                <HStack mt={2}>
                  {creatives.bannerImageUrl &&
                    !isDefaultCreatives.bannerImage &&
                    !bannerImageFile && (
                      <Button
                        variant={"link"}
                        colorScheme="red"
                        fontWeight="semibold"
                        textAlign={"left"}
                        alignSelf={"flex-start"}
                        zIndex={10}
                        isLoading={removingBannerImage}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveBannerImage();
                        }}
                      >
                        Remove Banner
                      </Button>
                    )}
                </HStack>
              </Flex>
            </Flex>
            <Flex
              gap={4}
              justifyContent="flex-start"
              alignItems="center"
              w="full"
              px={4}
              py={8}
              borderRadius="lg"
              transition="all 0.5s ease"
              _hover={{
                backgroundColor:
                  creatives.bannerImageUrl || bannerImage
                    ? "gray.100"
                    : "initial",
                cursor:
                  creatives.bannerImageUrl || bannerImage
                    ? "pointer"
                    : "not-allowed",
              }}
              onClick={triggerVideoUpload}
              as={motion.div}
            >
              <Flex
                boxSize={24}
                bgColor={
                  videoFile || creatives.bannerVideoUrl
                    ? "whatsapp.500"
                    : "gray.300"
                }
                borderRadius="full"
                alignItems="center"
                justifyContent="center"
              >
                <Heading
                  color={
                    creatives.bannerVideoUrl || videoFile ? "white" : "gray.700"
                  }
                >
                  2
                </Heading>
              </Flex>
              <Flex
                flexDir="column"
                justifyContent="center"
                px={4}
                borderLeftWidth={1}
                borderLeftColor="gray.200"
                w="70%"
              >
                <Flex gap={4} alignItems="center">
                  <Heading color="gray.700">Video</Heading>
                  <IconButton
                    isRound={true}
                    variant="solid"
                    colorScheme={"brand"}
                    aria-label="upload"
                    fontSize="16px"
                    size="sm"
                    icon={<FaPencilAlt style={{ marginBottom: 1 }} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      triggerVideoUpload();
                    }}
                  />
                  {videoFile && (
                    <Tooltip label="This button will allow you to clear the video you've selected just before.">
                      <Button
                        variant={"link"}
                        colorScheme="red"
                        fontWeight="semibold"
                        textAlign={"left"}
                        zIndex={10}
                        onClick={(e) => {
                          e.stopPropagation();
                          setVideoFile(null);
                          setVideoPreview(null);
                          videoInputRef.current!.value = "";
                        }}
                      >
                        Clear Selection
                      </Button>
                    </Tooltip>
                  )}
                </Flex>
                <Text color="gray.500" fontWeight="medium">
                  {videoFile || creatives.bannerVideoUrl
                    ? "Video set successfully. Click to select a different video (16:9 Aspect Ratio)"
                    : "Select a video for your campaign. This is optional but recommended (16:9 Aspect Ratio)"}
                </Text>
                <HStack mt={2}>
                  {creatives.bannerVideoUrl &&
                    !isDefaultCreatives.bannerVideo &&
                    !videoFile && (
                      <Button
                        variant={"link"}
                        colorScheme="red"
                        fontWeight="semibold"
                        textAlign={"left"}
                        alignSelf={"flex-start"}
                        zIndex={10}
                        isLoading={removingBannerVideo}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveBannerVideo();
                        }}
                      >
                        Remove Video
                      </Button>
                    )}
                </HStack>
              </Flex>
            </Flex>
          </Flex>
          <Flex
            flex={1}
            bgColor="gray.50"
            borderRadius={"lg"}
            h="full"
            alignItems={"center"}
            justifyContent="center"
            boxShadow={"sm"}
          >
            <Flex
              transform="scale(0.7)"
              bgColor="white"
              boxShadow="sm"
              borderWidth={1}
              borderColor="gray.200"
              borderRadius="2xl"
              flexDir="column"
              width={580}
              maxW={720}
              height={520}
              gap={8}
            >
              <Flex
                as={motion.div}
                initial={{ scale: 1 }}
                animate={{
                  scale: bannerImage || creatives.bannerImageUrl ? 1.051 : 1,
                }}
                transition="0.5s ease 0.5s"
                borderRadius="2xl"
                width="100%"
                height={300}
                bgColor={
                  bannerImage || creatives.bannerImageUrl
                    ? "transparent"
                    : "gray.200"
                }
                pos="relative"
                boxShadow={
                  bannerImage || creatives.bannerImageUrl
                    ? `0px 8px 24px ${hexToRGB("#422AFB", 0.25)}`
                    : "none"
                }
              >
                {bannerImage || creatives.bannerImageUrl ? (
                  <>
                    {(videoPreview || creatives.bannerVideoUrl) && (
                      <Flex
                        pos={"absolute"}
                        w="100%"
                        h={"100%"}
                        alignItems="center"
                        justifyContent={"center"}
                        bgColor="blackAlpha.200"
                        _hover={{
                          bgColor: "blackAlpha.600",
                          cursor: "pointer",
                        }}
                        borderRadius="2xl"
                        transition={"all 0.5s ease"}
                        onClick={handlePlayVideo}
                      >
                        <Flex
                          width={32}
                          height={32}
                          borderRadius="full"
                          alignItems="center"
                          justify={"center"}
                        >
                          <FaPlay size={64} color="white" />
                        </Flex>
                      </Flex>
                    )}
                    <Image
                      src={bannerImage ?? creatives.bannerImageUrl}
                      borderRadius="2xl"
                      width="100%"
                      height="100%"
                      objectFit="cover"
                    />
                  </>
                ) : (
                  <Text
                    flex={1}
                    alignSelf={"center"}
                    justifySelf="center"
                    textAlign={"center"}
                    fontWeight="medium"
                    color="gray.600"
                  >
                    Upload a banner image to see the preview here.
                  </Text>
                )}
              </Flex>
              <Flex
                flex={1}
                flexDir="column"
                px={6}
                gap={8}
                justifyContent="space-between"
                pb={8}
              >
                <Flex justifyContent="space-between">
                  <Flex flexDir="column" flex={2}>
                    <Heading noOfLines={1}>{campaign?.title}</Heading>
                    <Text fontSize="xl" color="gray.600">
                      New for you!
                    </Text>
                  </Flex>
                  <Flex flexDir="column" alignItems="flex-end" flex={1}>
                    <Flex alignItems="center" gap={1}>
                      <Text fontSize="3xl" fontWeight="bold">
                        +1090
                      </Text>
                      <Lottie
                        options={{
                          loop: true,
                          autoplay: true,
                          animationData: animationData,
                          rendererSettings: {
                            preserveAspectRatio: "xMidYMid slice",
                          },
                        }}
                        speed={0.4}
                        height={36}
                        width={36}
                      />
                      {/* <Image
                        src={require("../../../../../assets/img/dollar.png")}
                        boxSize={8}
                      /> */}
                    </Flex>
                    <Text
                      fontSize="lg"
                      color="gray.600"
                      mt={-1}
                      textAlign="center"
                    >
                      Coins per minute
                    </Text>
                  </Flex>
                </Flex>
                <Flex
                  _hover={{}}
                  flexDir="row"
                  textTransform="uppercase"
                  cursor="pointer"
                  as={motion.div}
                  width="100%"
                  px={4}
                  py={2}
                  bgGradient={`linear(to-tr, ${GRADIENT_BUTTON_1}, ${GRADIENT_BUTTON_2}, ${GRADIENT_BUTTON_3})`}
                  fontSize="3xl"
                  color="white"
                  fontWeight="bold"
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="full"
                  textShadow={`1px 1px ${hexToRGB(PURPLE, 0.8)}`}
                  boxShadow={`${hexToRGB(GRADIENT_BUTTON_2, 0.5)} 0px 8px 24px`}
                  gap={4}
                >
                  <Text>Play & Earn</Text>
                  {creatives?.iconUrl && (
                    <Image
                      src={creatives?.iconUrl}
                      boxSize={12}
                      borderRadius="full"
                    />
                  )}
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
        <GamelightButton
          isDisabled={
            (!bannerImage && !creatives.bannerImageUrl) || uploadingCreatives
          }
          alignSelf={"center"}
          isLoading={uploadingCreatives}
          w={"100%"}
          onClick={handleSaveCreatives}
        >
          Save Creatives
        </GamelightButton>
      </Flex>
      {RemoveBannerImageConfirmDialog}
      {RemoveBannerVideoConfirmDialog}
      <Modal isOpen={isOpen} onClose={handleCloseModal} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <ModalCloseButton />
          </ModalHeader>
          <ModalBody p={8}>
            <video
              ref={videoRef}
              src={videoPreview ?? creatives.bannerVideoUrl ?? ""}
              style={{ width: "100%", borderRadius: "0.5rem" }}
              controls
              autoPlay
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default Creatives;
